import { cva } from 'class-variance-authority';
import {
  createContext,
  useContext,
  useState,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';
import { Flex } from '../Flex/Flex.js';
import { clsx } from 'clsx';

import s from './Input.module.css';

const inputVariants = ['default', 'error'] as const;
type InputVariants = (typeof inputVariants)[number];

type InputRootContextType = {
  variant: InputVariants;
  count: number;
  maxLength?: number;
  setCount: (count: number) => void;
  setMaxLength: (maxLength: number | undefined) => void;
};

const InputRootContext = createContext<InputRootContextType | undefined>(
  undefined,
);

interface InputProviderProps {
  children: ReactNode;
  value: InputRootContextType;
}

const InputProvider = ({ children, value }: InputProviderProps) => {
  return (
    <InputRootContext.Provider value={value}>
      {children}
    </InputRootContext.Provider>
  );
};

const useInput = (): InputRootContextType => {
  const context = useContext(InputRootContext);

  if (context === undefined) {
    throw new Error('useInput must be used within a InputProvider');
  }

  return context;
};

const inputRootWidthOptions = ['default', 'full'] as const;
type InputRootWidth = (typeof inputRootWidthOptions)[number];

type InputRootProps = ComponentPropsWithRef<'div'> & {
  variant?: InputVariants;
  className?: string;
  children?: ReactNode;
  width?: InputRootWidth;
};

const inputRootVariants = cva(s.root, {
  variants: {
    width: {
      default: s.defaultWidth,
      full: s.fullWidth,
    },
    variant: {
      default: s.default,
      error: s.error,
    },
  },
  defaultVariants: {
    width: 'default',
  },
});

function InputRoot({
  variant = 'default',
  children,
  className,
  width = 'default',
  ...attrs
}: InputRootProps) {
  const [count, setCount] = useState<number>(0);
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);

  return (
    <InputProvider
      value={{ variant, count, maxLength, setCount, setMaxLength }}
    >
      <Flex
        direction="column"
        gap="g-6"
        className={clsx(inputRootVariants({ width, variant }), className)}
        {...attrs}
      >
        {children}
      </Flex>
    </InputProvider>
  );
}

export {
  InputRoot,
  InputProvider,
  useInput,
  inputRootWidthOptions,
  type InputRootWidth,
  type InputVariants,
};
